import React, { useRef, useState, useEffect } from 'react';
import { BiReply, BiX, BiImage, BiSend } from 'react-icons/bi';
import { insertChatMessage } from '../../api/chatApi';
import { useExamContext } from '../../context/ExamContext';

import './Chat.css';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export default function MessageInput({ onSend, replyTo, onClearReply, mentionableUsers = [], disabled }) {
  const [text, setText] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const mentionListRef = useRef(null);
  const ctx = useExamContext();
  let currentUserId = null;
  if(ctx?.role === 'student'){
    currentUserId = ctx?.decoded?.student_user_id;
  }else{
    currentUserId = ctx?.decoded?.user_id;
  }
  const roomId = ctx?.decoded ? (ctx.room_id ?? ctx.decoded.room_id) : null;
  const getMentionCandidates = () => {
    const q = (mentionFilter || '').toLowerCase().trim();
    const filtered = !q
      ? mentionableUsers
      : mentionableUsers.filter((u) => (u.name || '').toLowerCase().includes(q));
    const seen = new Set();
    return filtered.filter((u) => {
      const id = u?.id != null ? String(u.id) : null;
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const candidates = getMentionCandidates();

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!showMentionList) return;
    const first = mentionListRef.current?.querySelector('[data-mention-item]');
    if (first) first.focus?.();
    console.log('candidates', candidates);
  }, [showMentionList, candidates.length]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart ?? value.length;
    const beforeCursor = value.slice(0, cursor);
    const lastAt = beforeCursor.lastIndexOf('@');
    const charBeforeAt = lastAt > 0 ? value[lastAt - 1] : ' ';

    if (lastAt >= 0 && (lastAt === 0 || /\s/.test(charBeforeAt))) {
      const filter = beforeCursor.slice(lastAt + 1);
      const q = (filter || '').toLowerCase().trim();
      const hasFilter = q.length > 0;
      const matches = hasFilter
        ? mentionableUsers.some((u) => (u.name || '').toLowerCase().includes(q))
        : mentionableUsers.length > 0;
      setMentionStartIndex(lastAt);
      setMentionFilter(filter.trim());
      setShowMentionList(matches);
    } else {
      setShowMentionList(false);
      setMentionStartIndex(-1);
      setMentionFilter('');
    }
    setText(value);
  };

  const insertMention = (user) => {
    const cursor = inputRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, mentionStartIndex);
    const after = text.slice(cursor);
    const newText = `${before}@${user.name} ${after}`;
    setText(newText);
    setShowMentionList(false);
    setMentionFilter('');
    setMentionStartIndex(-1);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      const newCursor = mentionStartIndex + user.name.length + 2;
      inputRef.current?.setSelectionRange(newCursor, newCursor);
    });
    return user.id;
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      alert('Only images are allowed (JPEG, PNG, GIF, WebP).');
      e.target.value = '';
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async () => {
    const trimmed = text.trim();
    if (!trimmed && !imageFile) return;
    const senderId = currentUserId != null ? Number(currentUserId) : null;
    if (senderId == null || Number.isNaN(senderId)) {
      alert('Cannot send: you are not logged in.');
      return;
    }
    const mentionedUserIds = [];
    for (const u of mentionableUsers) {
      const escaped = u.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`@${escaped}(?:\\s|$)`);
      if (re.test(trimmed) && !mentionedUserIds.includes(u.id)) mentionedUserIds.push(u.id);
    }
    const payload = {
      sender_id: senderId,
      text: trimmed || '',
      reply_to_id: replyTo?.id ? Number(replyTo.id) : null,
      mentioned_user_ids: mentionedUserIds.map(Number),
      image: imageFile || null,
      room_id: roomId || '',
    };
    try {
      const sent = await insertChatMessage(payload);
      console.log('Message sent:', sent);
      if (typeof onSend === 'function') onSend(sent);
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error?.message || 'Failed to send message');
    }
    setText('');
    clearImage();
    onClearReply?.();
  };

  const handleKeyDown = (e) => {
    if (showMentionList) {
      if (e.key === 'Escape' || e.key === 'Tab') {
        setShowMentionList(false);
        setMentionStartIndex(-1);
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        return;
      }
    }
    if (!showMentionList && e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sendMessage();
  };

  return (
    <form className="chat-input-wrap p-2 border-t border-slate-200 bg-slate-50" onSubmit={handleSubmit}>
      {replyTo && (
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2 px-2">
          <BiReply className="shrink-0" />
          <span>Replying to {replyTo.user?.name}</span>
          <button type="button" className="p-0 ml-auto text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer text-sm" onClick={onClearReply}>Cancel</button>
        </div>
      )}
      {imagePreview && (
        <div className="relative inline-block mb-2">
          <img src={imagePreview} alt="Preview" className="chat-input-preview rounded-lg" />
          <button type="button" className="absolute top-0 right-0 m-1 p-1.5 rounded bg-red-600 hover:bg-red-700 text-white border-none cursor-pointer text-sm" onClick={clearImage} aria-label="Remove image">
            <BiX className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="chat-input-row">
        <div className="chat-input-with-mentions relative">
          <input
            ref={inputRef}
            type="text"
            className="w-full min-w-0 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={mentionableUsers.length ? `Use @ to mention${mentionableUsers.length === 1 ? ' staff' : ' students'}` : 'Type a message...'}
            value={text}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
          {showMentionList && candidates.length > 0 && (
            <ul ref={mentionListRef} className="chat-mention-list list-none m-0" role="listbox">
              {candidates.map((u) => (
                <li
                  key={u.id}
                  data-mention-item
                  role="option"
                  className="chat-mention-item"
                  onClick={() => insertMention(u)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      insertMention(u);
                    }
                  }}
                  tabIndex={0}
                >
                  {u.avatar ? (
                    <img src={u.avatar} alt="" className="chat-mention-avatar rounded-full" />
                  ) : (
                    <span className="chat-mention-avatar chat-mention-avatar-initial rounded-full">{((u.name || 'U').trim().charAt(0)).toUpperCase()}</span>
                  )}
                  <span>{u.name || 'User'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          onChange={handleImageChange}
          aria-label="Attach image"
        />
        <button
          type="button"
          className="shrink-0 px-2 py-1.5 text-sm border border-slate-300 rounded bg-white hover:bg-slate-50 text-slate-700"
          onClick={() => fileInputRef.current?.click()}
          title="Attach image only"
          disabled={disabled}
          style={{ display: 'none' }}
        >
          <BiImage className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="shrink-0 px-2 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white border-none disabled:opacity-50"
          disabled={disabled}
          onClick={handleSubmit}
        >
          <BiSend className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
